import type { GetStaticProps } from "next";
import dynamic from "next/dynamic";
import Link from "next/link";
import React, { useState } from "react";
import JsonLd from "@/components/JsonLd";
import SiteShell, { APP_URL } from "@/components/SiteShell";
import { toPublicMap, type PublicMap } from "@/funnel/publicMap";
import { SITE_URL, embedHowToLd } from "@/lib/structuredData";
import { getTemplate } from "@/templates";

// ReactFlow measures the DOM — client-only, like the app mapper.
const PublicMapViewer = dynamic(() => import("@/funnel/PublicMapViewer"), {
  ssr: false,
  loading: () => (
    <div className="flex h-full items-center justify-center text-[13px] text-slate-400">
      Loading map…
    </div>
  ),
});

const SNIPPET = `<iframe
  src="https://frazzlecrm.com/m/YOUR-TOKEN/embed"
  title="Funnel map — FrazzleCRM"
  width="100%" height="520" loading="lazy"
  style="border:1px solid #e2e8f0;border-radius:12px"
></iframe>`;

const STEPS = [
  {
    title: "Share the map",
    body: "Open your map in FrazzleCRM and hit Share. That mints a public read-only link — automation content never leaves your workspace.",
  },
  {
    title: "Copy the embed code",
    body: "The same popover hands you a ready-made iframe snippet pointing at /m/<token>/embed. One click to copy.",
  },
  {
    title: "Paste it into your page",
    body: "Anywhere an iframe is allowed: a WordPress HTML block, a Webflow embed, Notion’s /embed, MDX docs, a Google Site. Edit the map later and the embed shows the new version.",
  },
];

type Props = { map: PublicMap };

/** The template this page demos — also the map on its social card. */
const DEMO_TEMPLATE_SLUG = "lead-magnet-nurture";

export const getStaticProps: GetStaticProps<Props> = () => {
  // The demo below is a real template map rendered through the same public
  // sanitizer the embed uses — not a screenshot, and not a customer's map.
  const t = getTemplate(DEMO_TEMPLATE_SLUG);
  if (!t) return { notFound: true };
  return {
    props: {
      map: toPublicMap({
        id: t.slug,
        name: t.name,
        createdAt: "",
        updatedAt: "",
        flow: t.flow,
      }),
    },
  };
};

export default function EmbedPage({ map }: Props) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SNIPPET);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be unavailable — the block below stays selectable.
    }
  };

  return (
    <SiteShell
      title="Embed a live funnel map on your site — FrazzleCRM"
      description="Embed a live, pannable funnel map in any page with one iframe. Free on every FrazzleCRM plan — read-only, always current, automation content stays private."
      canonical={`${SITE_URL}/embed`}
      ogTitle="Embed a live funnel map on your site — free"
      /* The card shows the very template this page demos. */
      ogImage={`${SITE_URL}/og/template/${DEMO_TEMPLATE_SLUG}.png`}
      ogImageAlt="A FrazzleCRM funnel map embedded on a page"
    >
      <JsonLd data={embedHowToLd()} />

      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-3xl px-6 pb-10 pt-16 text-center">
          <h1 className="text-[34px] font-extrabold leading-[1.12] tracking-tight text-slate-900 sm:text-[44px]">
            Put a{" "}
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              live funnel map
            </span>{" "}
            on your site
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-[15.5px] leading-relaxed text-slate-600">
            One iframe. A real, pannable diagram instead of a screenshot that
            goes stale the day after you take it. Free on every plan —
            including the free one.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6">
        <div className="h-[420px] overflow-hidden rounded-2xl border border-slate-200 shadow-sm sm:h-[520px]">
          <PublicMapViewer map={map} />
        </div>
        <p className="mt-2 text-center text-[12px] text-slate-400">
          The {map.name} template, rendered exactly as an embed renders it —
          pan and zoom it. Yours would be your own map.
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-14">
        <h2 className="text-[22px] font-bold tracking-tight text-slate-900">
          Three steps
        </h2>
        <ol className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
          {STEPS.map((s, i) => (
            <li
              key={s.title}
              className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-600 text-[13px] font-bold text-white">
                {i + 1}
              </span>
              <h3 className="mt-3 text-[15.5px] font-bold text-slate-900">
                {s.title}
              </h3>
              <p className="mt-1.5 text-[13.5px] leading-relaxed text-slate-600">
                {s.body}
              </p>
            </li>
          ))}
        </ol>

        <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-900 p-5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[12.5px] font-semibold text-slate-300">
              The snippet
            </span>
            <button
              onClick={copy}
              className="min-h-[36px] rounded-lg bg-white/10 px-3 text-[12px] font-semibold text-white hover:bg-white/20"
            >
              {copied ? "Copied ✓" : "Copy"}
            </button>
          </div>
          <pre className="mt-3 overflow-x-auto text-[12px] leading-relaxed text-slate-200">
            <code>{SNIPPET}</code>
          </pre>
          <p className="mt-3 text-[12px] leading-relaxed text-slate-400">
            Swap <code className="text-slate-200">YOUR-TOKEN</code> for the
            token in your share link (or just copy the ready-made snippet from
            the Share popover). Height is yours to pick — iframes can&apos;t
            size themselves, so 400–600px suits most maps.
          </p>
        </div>
      </section>

      <section className="border-t border-slate-100 bg-slate-50/60">
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 px-6 py-14 md:grid-cols-2">
          <div>
            <h2 className="text-[19px] font-bold text-slate-900">
              What visitors see
            </h2>
            <ul className="mt-4 space-y-2.5 text-[14px] text-slate-600">
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-emerald-500">✓</span> The map:
                structure, labels, notes and doc links, pannable and zoomable.
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-emerald-500">✓</span> A small
                &ldquo;Built with FrazzleCRM&rdquo; badge. It&apos;s the price
                of the free embed, and it isn&apos;t removable.
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-emerald-500">✓</span> A
                &ldquo;Copy this map&rdquo; button — only if you ticked{" "}
                <em>let visitors copy this map</em> on the share link.
              </li>
            </ul>
          </div>
          <div>
            <h2 className="text-[19px] font-bold text-slate-900">
              What they never see
            </h2>
            <ul className="mt-4 space-y-2.5 text-[14px] text-slate-600">
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-slate-400">✕</span> Email and SMS
                content, subjects, senders or trigger configuration.
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-slate-400">✕</span> Contacts,
                traffic, run history — none of it is in the map file at all.
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-slate-400">✕</span> Anything
                editable. The embed is strictly read-only, and revoking the
                share link turns every copy of it off at once.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-14">
        <h2 className="text-[22px] font-bold tracking-tight text-slate-900">
          Honest notes
        </h2>
        <div className="mt-5 space-y-4 text-[14px] leading-relaxed text-slate-600">
          <p>
            <strong className="font-semibold text-slate-800">
              The embed is unlisted, not secret.
            </strong>{" "}
            Anyone with the token can view it, and the page is{" "}
            <code className="rounded bg-slate-100 px-1 py-0.5 text-[12.5px]">
              noindex
            </code>{" "}
            so it never competes with your page in search. Treat a share token
            like a link to an unlisted video.
          </p>
          <p>
            <strong className="font-semibold text-slate-800">
              It renders client-side.
            </strong>{" "}
            The diagram is drawn in the browser, so search engines index your
            surrounding words, not the map. Write a sentence about the funnel
            next to the embed — that&apos;s what gets read.
          </p>
          <p>
            <strong className="font-semibold text-slate-800">
              Fixed height.
            </strong>{" "}
            Cross-origin iframes can&apos;t auto-size, and we don&apos;t ask
            you to paste a script to work around it. Pick a height.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <a
            href={`${APP_URL}/signup`}
            className="rounded-xl bg-indigo-600 px-5 py-2.5 text-[14px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"
          >
            Draw a map — free
          </a>
          <Link
            href="/tools/funnel-generator"
            className="rounded-xl border border-slate-200 px-5 py-2.5 text-[14px] font-semibold text-slate-700 hover:bg-slate-50"
          >
            Or generate one from a sentence
          </Link>
        </div>

        <p className="mt-8 text-[13.5px] leading-relaxed text-slate-500">
          Nothing to embed yet? Start from a{" "}
          <Link
            href="/templates"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            funnel template
          </Link>
          , see{" "}
          <Link
            href="/pricing"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            what the plans cost
          </Link>
          , or read why{" "}
          <Link
            href="/blog/two-way-sync-diagram-is-the-automation"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            the diagram IS the automation
          </Link>
          .
        </p>
      </section>
    </SiteShell>
  );
}
